import { GoogleTagEventName, GtmEventName } from './gtm-event-name.enum';
import { getPwaDisplayMode, markPwaMeaningfulEarn } from 'src/pwa/pwa';

type GoogleTagWindow = Window & {
  dataLayer?: Array<Record<string, unknown> | IArguments>;
};

const firedDataLayerEvents = new Set<GtmEventName>();
const firedGoogleTagEvents = new Set<GoogleTagEventName>();

/**
 * Pushes a named event to window.dataLayer exactly once per SPA session.
 * Subsequent calls with the same event name are silently ignored.
 */
export function pushToDataLayer(eventName: GtmEventName): void {
  if (firedDataLayerEvents.has(eventName)) {
    return;
  }

  firedDataLayerEvents.add(eventName);
  (window as GoogleTagWindow).dataLayer?.push({ event: eventName });
}

/**
 * Pushes a named event to window.dataLayer every time it is called.
 */
export function pushRepeatableDataLayerEvent(
  eventName: GtmEventName,
  eventParameters: Record<string, unknown> = {}
): void {
  (window as GoogleTagWindow).dataLayer?.push({ event: eventName, ...eventParameters });
}

/**
 * Sends a Google tag event exactly once per SPA session via dataLayer for GTM.
 */
export function pushGoogleTagEvent(eventName: GoogleTagEventName, eventParameters: Record<string, unknown> = {}): void {
  if (firedGoogleTagEvents.has(eventName)) {
    return;
  }

  firedGoogleTagEvents.add(eventName);
  (window as GoogleTagWindow).dataLayer?.push({ event: eventName, ...eventParameters });
}

export function pushRegistrationConversionEvents(): void {
  pushToDataLayer(GtmEventName.Registration);
  pushGoogleTagEvent(GoogleTagEventName.AdsConversionSignup);
}

export function pushFirstProjectCompleteConversionEvents(): void {
  markPwaMeaningfulEarn('survey_completed');
  pushRepeatableDataLayerEvent(GtmEventName.SurveyCompleted);
  pushToDataLayer(GtmEventName.FirstProjectComplete);
  pushToDataLayer(GtmEventName.Survey1Submit);
  pushGoogleTagEvent(GoogleTagEventName.AdsConversion);
}

export function pushSecondProjectCompleteConversionEvents(): void {
  markPwaMeaningfulEarn('survey_completed');
  pushRepeatableDataLayerEvent(GtmEventName.SurveyCompleted);
  pushToDataLayer(GtmEventName.SecondProjectComplete);
  pushToDataLayer(GtmEventName.Survey2Submit);
}

export function pushSurveyCompletedEvents(): void {
  markPwaMeaningfulEarn('survey_completed');
  pushRepeatableDataLayerEvent(GtmEventName.SurveyCompleted);
}

export function pushPwaInstalledLaunchEvent(): void {
  if (getPwaDisplayMode() !== 'standalone') {
    return;
  }

  const searchParameters = new URLSearchParams(window.location.search);
  const shortcut = searchParameters.get('shortcut');

  pushRepeatableDataLayerEvent(GtmEventName.PwaInstalledLaunch, {
    display_mode: 'standalone',
    source: searchParameters.get('source') ?? 'standalone',
    ...(shortcut ? { shortcut } : {}),
  });
}

export function pushPwaPartnerActivityEvent(source: string): void {
  pushRepeatableDataLayerEvent(GtmEventName.PwaPartnerActivity, {
    display_mode: getPwaDisplayMode(),
    source,
  });
}
